import { useState, useEffect, useRef } from "react";
import { Send, Plus, Trash2, ChevronDown, Globe, Loader2, Paperclip, X, FileText, Image as ImageIcon, ArrowRight, Lightbulb, MessageSquare } from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import askfintLogo from "@/assets/askfint-logo-new.png";
import { useCredits } from "@/hooks/useCredits";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface Message {
  role: "user" | "assistant";
  content: string;
  isThinking?: boolean;
  attachments?: UploadedFile[];
}

interface Conversation {
  id: string;
  title: string;
  created_at: string;
}

interface UploadedFile {
  name: string;
  type: string;
  size: number;
  dataUrl: string;
}

const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain,text/csv,application/json";
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const STARTER_PROMPTS = [
  { emoji: "💡", text: "What product should I create for my niche?" },
  { emoji: "📋", text: "Review my content strategy" },
  { emoji: "💰", text: "How do I price my first product?" },
  { emoji: "🚀", text: "Help me write a launch plan" },
  { emoji: "🎯", text: "What's the best platform for my audience?" },
  { emoji: "📈", text: "How do I grow from 500 to 5,000 followers?" },
  { emoji: "🔍", text: "Analyse my niche competition" },
  { emoji: "✉️", text: "Help me write my first email sequence" },
];

// Fetch user context from other tools
async function fetchUserContext(userId: string): Promise<string> {
  const parts: string[] = [];

  try {
    // Get profile/niche info
    const { data: profile } = await supabase
      .from("profiles")
      .select("display_name")
      .eq("user_id", userId)
      .single();

    if (profile?.display_name) {
      parts.push(`User's display name: ${profile.display_name}`);
    }

    // Get monetization projects (products they've built)
    const { data: monProjects } = await supabase
      .from("monetization_projects")
      .select("title, niche, audience_type, platform, entry_type")
      .order("created_at", { ascending: false })
      .limit(5);

    if (monProjects && monProjects.length > 0) {
      const projectSummaries = monProjects.map(p =>
        `"${p.title}" (${p.entry_type}, niche: ${p.niche || "unknown"}, audience: ${p.audience_type || "unknown"}, platform: ${p.platform || "unknown"})`
      ).join("; ");
      parts.push(`Monetisation AI projects: ${projectSummaries}`);
    }

    // Get monetization products
    const { data: monProducts } = await supabase
      .from("monetization_products")
      .select("title, product_type, status, fit_score")
      .order("created_at", { ascending: false })
      .limit(5);

    if (monProducts && monProducts.length > 0) {
      const productSummaries = monProducts.map(p =>
        `"${p.title}" (${p.product_type}, status: ${p.status}, fit: ${p.fit_score}%)`
      ).join("; ");
      parts.push(`Products built: ${productSummaries}`);
    }

    // Get Nexis AI projects (scripts)
    const { data: nexisProjects } = await supabase
      .from("nexis_projects")
      .select("title, niche, platform, tone")
      .order("created_at", { ascending: false })
      .limit(5);

    if (nexisProjects && nexisProjects.length > 0) {
      const scriptSummaries = nexisProjects.map(p =>
        `"${p.title}" (niche: ${p.niche || "unknown"}, platform: ${p.platform || "unknown"})`
      ).join("; ");
      parts.push(`Nexis AI scripts: ${scriptSummaries}`);
    }

    // Get keyword research
    const { data: keywords } = await supabase
      .from("saved_keywords")
      .select("keyword, niche")
      .order("created_at", { ascending: false })
      .limit(10);

    if (keywords && keywords.length > 0) {
      parts.push(`Saved keywords: ${keywords.map(k => k.keyword).join(", ")}`);
    }
  } catch (err) {
    console.error("Error fetching user context:", err);
  }

  return parts.length > 0 ? parts.join("\n") : "";
}

// Generate a short topic label from a message
async function generateConversationTitle(firstMessage: string): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke("askfint-chat", {
      body: {
        messages: [{ role: "user", content: firstMessage }],
        generateTitle: true,
      },
    });
    if (!error && data?.title) return data.title;
  } catch {
    // fallback
  }
  // Fallback: extract meaningful snippet
  const cleaned = firstMessage.replace(/\s+/g, " ").trim();
  if (cleaned.length <= 40) return cleaned;
  return cleaned.slice(0, 37) + "...";
}

function ThinkingIndicator() {
  const [isOpen, setIsOpen] = useState(true);
  const [activeStep, setActiveStep] = useState(1);

  useEffect(() => {
    const t1 = setTimeout(() => setActiveStep(2), 600);
    const t2 = setTimeout(() => setActiveStep(3), 1400);
    const t3 = setTimeout(() => setActiveStep(4), 2200);
    const t4 = setTimeout(() => setActiveStep(5), 3200);
    const t5 = setTimeout(() => setActiveStep(6), 4500);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); clearTimeout(t5); };
  }, []);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full text-left group">
        <span className="text-sm font-semibold text-foreground">Researching & thinking deeply...</span>
        <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 space-y-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Globe className="w-3.5 h-3.5 text-primary animate-pulse" />
          <span>🔍 Research Agent — Searching knowledge sources & data...</span>
        </div>
        {activeStep >= 2 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground animate-in fade-in slide-in-from-left-2 duration-300">
            <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />
            <span>📊 Data Agent — Analyzing trends, stats & case studies...</span>
          </div>
        )}
        {activeStep >= 3 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground animate-in fade-in slide-in-from-left-2 duration-300">
            <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />
            <span>🧠 Strategy Agent — Evaluating approaches & frameworks...</span>
          </div>
        )}
        {activeStep >= 4 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground animate-in fade-in slide-in-from-left-2 duration-300">
            <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />
            <span>🎯 Content Agent — Cross-referencing best practices...</span>
          </div>
        )}
        {activeStep >= 5 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground animate-in fade-in slide-in-from-left-2 duration-300">
            <Loader2 className="w-3.5 h-3.5 text-primary animate-spin" />
            <span>💰 Monetization Agent — Identifying revenue opportunities...</span>
          </div>
        )}
        {activeStep >= 6 && (
          <div className="text-[10px] text-muted-foreground/60 mt-1 pl-5 animate-in fade-in duration-500">
            ✨ All agents reporting in — synthesizing final answer...
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

function FilePreview({ file, onRemove }: { file: UploadedFile; onRemove: () => void }) {
  const isImage = file.type.startsWith("image/");
  return (
    <div className="relative group flex items-center gap-2 bg-muted/80 rounded-lg px-3 py-2 border border-border">
      {isImage ? (
        <img src={file.dataUrl} alt={file.name} className="w-10 h-10 rounded object-cover" />
      ) : (
        <FileText className="w-8 h-8 text-muted-foreground flex-shrink-0" />
      )}
      <div className="min-w-0">
        <p className="text-xs font-medium text-foreground truncate max-w-[120px]">{file.name}</p>
        <p className="text-[10px] text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</p>
      </div>
      <button
        onClick={onRemove}
        className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

// Parse "Send to..." action buttons from assistant content
function ActionButtons({ content }: { content: string }) {
  const navigate = useNavigate();
  const actions: { label: string; url: string }[] = [];

  const lower = content.toLowerCase();
  if (lower.includes("product") || lower.includes("monetis") || lower.includes("digital product") || lower.includes("course") || lower.includes("ebook") || lower.includes("template")) {
    actions.push({ label: "Create in Monetisation AI →", url: "/app/monetization-ai" });
  }
  if (lower.includes("script") || lower.includes("video idea") || lower.includes("content idea") || lower.includes("youtube video")) {
    actions.push({ label: "Write script in Nexis AI →", url: "/app/nexis-ai" });
  }
  if (lower.includes("sales page") || lower.includes("landing page")) {
    actions.push({ label: "Build Sales Page →", url: "/app/sales-page-builder" });
  }
  if (lower.includes("email") || lower.includes("newsletter") || lower.includes("email list")) {
    actions.push({ label: "Build Email Sequence →", url: "/app/email-list-builder" });
  }
  if (lower.includes("launch") || lower.includes("launch plan") || lower.includes("launch strategy")) {
    actions.push({ label: "Plan Launch →", url: "/app/launch-advisor" });
  }
  if (lower.includes("keyword") || lower.includes("seo") || lower.includes("search term")) {
    actions.push({ label: "Research Keywords →", url: "/app/keywords" });
  }

  // Deduplicate and limit to 3
  const unique = actions.filter((a, i) => actions.findIndex(b => b.url === a.url) === i).slice(0, 3);

  if (unique.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border/50">
      {unique.map((action) => (
        <button
          key={action.url}
          onClick={() => navigate(action.url)}
          className="flex items-center gap-1.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg px-3 py-1.5 transition-colors"
        >
          <ArrowRight className="w-3 h-3" />
          {action.label}
        </button>
      ))}
    </div>
  );
}

export default function AskFint() {
  const { user } = useAuth();
  const { consume } = useCredits();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [userContext, setUserContext] = useState<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pre-fill from floating button context
  useEffect(() => {
    const context = searchParams.get("context");
    if (context) {
      setInput(context);
      // Clean URL
      searchParams.delete("context");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  const isEmptyState = !currentConversationId && messages.length === 0;

  // Load user context on mount
  useEffect(() => {
    if (user) {
      fetchUserContext(user.id).then(setUserContext);
    }
  }, [user]);

  // Load conversations
  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadConversations = async () => {
    const { data, error } = await supabase
      .from("conversations")
      .select("*")
      .order("updated_at", { ascending: false });

    if (error) {
      console.error("Error loading conversations:", error);
      return;
    }

    setConversations(data || []);
  };

  const loadMessages = async (conversationId: string) => {
    const { data, error } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error loading messages:", error);
      return;
    }

    if (data && data.length > 0) {
      setMessages(data.map(m => ({ role: m.role as "user" | "assistant", content: m.content })));
    } else {
      setMessages([]);
    }
    setCurrentConversationId(conversationId);
  };

  const startNewConversation = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setUploadedFiles([]);
    setInput("");
  };

  const deleteConversation = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const { error } = await supabase
      .from("conversations")
      .delete()
      .eq("id", id);

    if (error) {
      toast.error("Failed to delete conversation");
      return;
    }

    setConversations(prev => prev.filter(c => c.id !== id));
    if (currentConversationId === id) {
      startNewConversation();
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Max 10MB.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setUploadedFiles(prev => [
          ...prev,
          { name: file.name, type: file.type, size: file.size, dataUrl: reader.result as string }
        ]);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = "";
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const streamChat = async (allMessages: Message[]) => {
    const apiMessages = allMessages.map(m => {
      if (m.attachments && m.attachments.length > 0) {
        const fileDescriptions = m.attachments.map(f => {
          if (f.type.startsWith("image/")) {
            return `[User attached an image: ${f.name}]`;
          }
          return `[User attached a file: ${f.name} (${f.type})]`;
        }).join("\n");
        return { role: m.role, content: `${fileDescriptions}\n\n${m.content}` };
      }
      return { role: m.role, content: m.content };
    });

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/askfint-chat`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      },
      body: JSON.stringify({ messages: apiMessages, userContext }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to get response");
    }

    return response;
  };

  const sendMessage = async (messageText: string) => {
    if ((!messageText.trim() && uploadedFiles.length === 0) || isLoading || !user) return;
    const credited = await consume("askfint-chat", "AskFint chat");
    if (!credited) return;

    let convId = currentConversationId;
    const isNewConversation = !convId;

    // Create conversation if none exists
    if (!convId) {
      const tempTitle = messageText.trim() ? messageText.slice(0, 50) : (uploadedFiles[0]?.name || "New Conversation");
      const { data, error } = await supabase
        .from("conversations")
        .insert({ user_id: user.id, title: tempTitle })
        .select()
        .single();

      if (error) {
        toast.error("Failed to create conversation");
        return;
      }

      convId = data.id;
      setCurrentConversationId(convId);
      setConversations(prev => [data, ...prev]);
    }

    const userMessage: Message = {
      role: "user",
      content: messageText,
      attachments: uploadedFiles.length > 0 ? [...uploadedFiles] : undefined,
    };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setUploadedFiles([]);
    setIsLoading(true);

    // Save user message
    const savedContent = userMessage.attachments
      ? `${userMessage.attachments.map(f => `[Attached: ${f.name}]`).join("\n")}\n\n${messageText}`
      : messageText;

    await supabase.from("messages").insert({
      conversation_id: convId,
      role: "user",
      content: savedContent,
    });

    try {
      const response = await streamChat(newMessages.filter(m => !m.isThinking));
      
      if (!response.body) throw new Error("No response body");

      setMessages(prev => [...prev, { role: "assistant", content: "", isThinking: true }]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let assistantContent = "";
      let firstTokenReceived = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              if (!firstTokenReceived) {
                firstTokenReceived = true;
              }
              assistantContent += content;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: "assistant", content: assistantContent, isThinking: false };
                return updated;
              });
            }
          } catch {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }

      // Save assistant message
      if (assistantContent) {
        await supabase.from("messages").insert({
          conversation_id: convId,
          role: "assistant",
          content: assistantContent,
        });

        // Auto-generate a topic label for new conversations
        if (isNewConversation && messageText.trim()) {
          generateConversationTitle(messageText).then(async (title) => {
            await supabase
              .from("conversations")
              .update({ title })
              .eq("id", convId!);
            setConversations(prev => prev.map(c => c.id === convId ? { ...c, title } : c));
          });
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to get response");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = () => sendMessage(input);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStarterClick = (text: string) => {
    sendMessage(text);
  };

  // Shared input bar component
  const renderInputBar = (centered?: boolean) => (
    <div className={cn("w-full", centered ? "max-w-2xl" : "max-w-3xl mx-auto")}>
      {uploadedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {uploadedFiles.map((file, i) => (
            <FilePreview key={i} file={file} onRemove={() => removeFile(i)} />
          ))}
        </div>
      )}
      <div className="relative flex items-center gap-2 bg-muted border border-border rounded-xl px-3 py-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          onChange={handleFileUpload}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-2 rounded-lg hover:bg-background/50 transition-colors text-muted-foreground hover:text-foreground"
          title="Attach image or file"
        >
          <Paperclip className="w-5 h-5" />
        </button>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="What do you want to know?"
          className="flex-1 bg-transparent border-none outline-none text-sm text-foreground placeholder:text-muted-foreground h-10"
        />
        <Button
          onClick={handleSend}
          disabled={(!input.trim() && uploadedFiles.length === 0) || isLoading}
          size="icon"
          className="h-9 w-9 rounded-lg flex-shrink-0"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="h-[calc(100vh-4rem)] flex">
      {/* Sidebar - Conversations */}
      <div className="w-64 border-r border-border p-4 hidden lg:flex flex-col">
        <Button variant="outline" className="w-full mb-4" onClick={startNewConversation}>
          <Plus className="w-4 h-4 mr-2" />
          New Conversation
        </Button>
        <div className="flex-1 overflow-auto space-y-2">
          {conversations.map((conv) => (
            <div
              key={conv.id}
              onClick={() => loadMessages(conv.id)}
              className={cn(
                "w-full text-left p-3 rounded-lg transition-colors group relative cursor-pointer",
                currentConversationId === conv.id
                  ? "bg-primary/10 border border-primary/20"
                  : "bg-muted/50 hover:bg-muted"
              )}
            >
              <p className="text-sm font-medium text-foreground truncate pr-6">{conv.title}</p>
              <p className="text-xs text-muted-foreground">
                {new Date(conv.created_at).toLocaleDateString()}
              </p>
              <button
                onClick={(e) => deleteConversation(conv.id, e)}
                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-destructive/20 rounded"
              >
                <Trash2 className="w-4 h-4 text-destructive" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {isEmptyState ? (
          /* Centered empty state with starters */
          <div className="flex-1 flex flex-col items-center justify-center px-6 overflow-auto">
            <img
              src={askfintLogo}
              alt="AskFint"
              className="w-20 h-20 rounded-full object-cover mb-3"
            />
            <h1 className="text-2xl font-bold text-foreground mb-1">AskFint — AI Mentor</h1>
            <p className="text-sm text-muted-foreground mb-8">Your AI mentor for content creation & monetization</p>

            {/* Starter prompts */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl w-full mb-8">
              {STARTER_PROMPTS.map((prompt) => (
                <button
                  key={prompt.text}
                  onClick={() => handleStarterClick(prompt.text)}
                  disabled={isLoading}
                  className="flex items-center gap-3 text-left px-4 py-3 rounded-xl border border-border bg-muted/30 hover:bg-muted hover:border-primary/30 transition-all text-sm text-foreground group"
                >
                  <span className="text-lg flex-shrink-0">{prompt.emoji}</span>
                  <span className="group-hover:text-primary transition-colors">{prompt.text}</span>
                </button>
              ))}
            </div>

            {/* File analysis tip */}
            <div className="flex items-start gap-3 max-w-2xl w-full bg-primary/5 border border-primary/10 rounded-xl px-4 py-3 mb-6">
              <Lightbulb className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Upload for specific feedback</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Attach a screenshot of your analytics, a competitor's page, or your draft product — I'll give you specific, actionable feedback.
                </p>
              </div>
            </div>

            {renderInputBar(true)}
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center gap-3">
              <img 
                src={askfintLogo} 
                alt="AskFint" 
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <h1 className="font-semibold text-foreground">AskFint — AI Mentor</h1>
                <p className="text-xs text-muted-foreground">Your AI mentor for content creation & monetization</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {messages.map((message, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex gap-3 max-w-3xl",
                    message.role === "user" ? "ml-auto flex-row-reverse" : ""
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden",
                    message.role === "user" ? "bg-primary" : ""
                  )}>
                    {message.role === "user" ? (
                      <span className="text-xs font-bold text-primary-foreground">U</span>
                    ) : (
                      <img src={askfintLogo} alt="Fint" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className={cn(
                    "rounded-2xl px-4 py-3",
                    message.role === "user" 
                      ? "bg-primary text-primary-foreground max-w-lg" 
                      : "bg-muted text-foreground max-w-2xl"
                  )}>
                    {/* Show attachments for user messages */}
                    {message.role === "user" && message.attachments && message.attachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {message.attachments.map((file, fi) => (
                          <div key={fi} className="flex items-center gap-1.5 bg-primary-foreground/10 rounded-md px-2 py-1">
                            {file.type.startsWith("image/") ? (
                              <img src={file.dataUrl} alt={file.name} className="w-16 h-16 rounded object-cover" />
                            ) : (
                              <>
                                <FileText className="w-3.5 h-3.5" />
                                <span className="text-xs truncate max-w-[100px]">{file.name}</span>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {message.role === "user" ? (
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                    ) : message.isThinking ? (
                      <ThinkingIndicator />
                    ) : (
                      <>
                        <div className="prose prose-sm prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_p]:text-sm [&_p]:leading-relaxed [&_li]:text-sm [&_h1]:text-lg [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-base [&_h2]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_code]:text-xs [&_code]:bg-background/50 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_pre]:bg-background/50 [&_pre]:rounded-lg [&_pre]:p-3 [&_strong]:text-foreground [&_strong]:font-semibold [&_ul]:space-y-1 [&_ol]:space-y-1 [&_blockquote]:border-l-2 [&_blockquote]:border-primary/50 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_hr]:border-border/50 [&_hr]:my-4 [&_em]:text-foreground/80">
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </div>
                        {!isLoading && message.content.length > 100 && (
                          <ActionButtons content={message.content} />
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
                <div className="flex gap-3 max-w-3xl">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <img src={askfintLogo} alt="Fint" className="w-full h-full object-cover" />
                  </div>
                  <div className="bg-muted rounded-2xl px-4 py-3">
                    <ThinkingIndicator />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-4 border-t border-border">
              {renderInputBar()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
